/**
 * Prijshistorie (US-023, design-handoff scherm 6).
 *
 * Eén rij per kalenderdag, één kolom per bron. De laagste prijs van een dag wordt geel
 * gemarkeerd, wijzigingen krijgen een pijl, en lege metingen blijven zichtbaar — die vertellen
 * dat er wél is gemeten maar niets is gevonden.
 */

import {
  addDays,
  formatDayLabel,
  formatEuro,
  MEASUREMENT_STATUS_LABELS,
  sorteerOpPrijs,
  toLocalDateKey,
  type MeasurementStatus,
} from '@prijsmonitor/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { api, type ApiError } from '../api/client.js';
import { BronLabel } from '../components/BronLabel.js';
import { Button, Notice, ui } from '../components/ui/index.js';
import { bereikVoor, PERIODES, type Periode } from '../lib/periode.js';
import styles from './HistoryScreen.module.css';

interface HistoryCell {
  sourceId: string;
  priceCents: number | null;
  status: MeasurementStatus | null;
  changeDirection: 'NONE' | 'UP' | 'DOWN';
  isChange: boolean;
  isLowest: boolean;
}

interface History {
  productId: string;
  productName: string;
  ownSku: string;
  columns: Array<{
    sourceId: string;
    name: string;
    isOwn: boolean;
    url?: string | null;
    domain?: string | null;
  }>;
  days: Array<{ dateKey: string; cells: HistoryCell[]; hasChange: boolean }>;
  summary: { dayCount: number; changeDayCount: number };
}

interface Product {
  id: string;
  name: string;
  ownSku: string;
}

export function HistoryScreen(): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const [products, setProducts] = useState<Product[]>([]);
  const [history, setHistory] = useState<History | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const productId = params.get('product') ?? '';
  const preset = (params.get('periode') ?? '30') as Periode;
  const alleenWijzigingen = params.get('alleenWijzigingen') === 'true';
  const opNaam = params.get('sortering') === 'naam';

  /*
   * De kolommen op de prijs van de bovenste rij — de laatste dag in de periode, want de tabel loopt
   * van nieuw naar oud. Zo lopen de bedragen op die rij van links naar rechts op, en zie je in één
   * blik wie er onder ons zit.
   *
   * Op de dagen daaronder is de volgorde daardoor niet per se oplopend, en dat is juist bruikbaar:
   * een bron die op de bovenste rij vooraan staat maar verder terug rechts had gestaan, is recent
   * gezakt.
   */
  const ordening = useMemo(() => {
    if (!history) return [];
    const indexen = history.columns.map((_, index) => index);
    if (opNaam) return indexen;

    // Op index en niet op sourceId: de afspraak die overal in deze API geldt, is dat `cells` in de
    // volgorde van `columns` staat. Zie de toelichting in OverviewScreen, waar dat verschil bijt.
    const laatsteDag = history.days[0];
    return sorteerOpPrijs(
      indexen,
      (index) => history.columns[index]?.isOwn ?? false,
      (index) => laatsteDag?.cells[index]?.priceCents ?? null,
    );
  }, [history, opNaam]);

  const bereik = bereikVoor(preset) ?? {
    van: params.get('van') ?? addDays(toLocalDateKey(), -29),
    tot: params.get('tot') ?? toLocalDateKey(),
  };

  useEffect(() => {
    api
      .get<{ products: Product[] }>('/api/products?status=ACTIVE')
      .then((r) => {
        setProducts(r.products);
        if (!productId && r.products[0]) {
          setParams({ product: r.products[0].id }, { replace: true });
        }
      })
      .catch((e: ApiError) => setError(e.message));
    // Bewust alleen bij het openen van het scherm: de productlijst verandert niet tijdens
    // het bekijken van de historie.
  }, []);

  const update = useCallback(
    (next: Record<string, string>) => {
      const query = new URLSearchParams(params);
      for (const [key, value] of Object.entries(next)) {
        if (value) query.set(key, value);
        else query.delete(key);
      }
      setParams(query, { replace: true });
    },
    [params, setParams],
  );

  useEffect(() => {
    if (!productId) return;
    const query = new URLSearchParams({ van: bereik.van, tot: bereik.tot });
    if (alleenWijzigingen) query.set('alleenWijzigingen', 'true');

    api
      .get<History>(`/api/products/${productId}/historie?${query.toString()}`)
      .then(setHistory)
      .catch((e: ApiError) => setError(e.message));
  }, [productId, bereik.van, bereik.tot, alleenWijzigingen]);

  /**
   * Naar de grafiekweergave, met hetzelfde product en dezelfde periode.
   *
   * De datums gaan altijd mee, ook bij een periodeknop. De twee schermen delen die knoppen nu, dus
   * strikt genomen is de naam genoeg — maar een oude bladwijzer kan een sleutel bevatten die niet
   * meer bestaat, en dan zijn de datums het enige dat het bereik nog vasthoudt.
   */
  function naarGrafiek(): void {
    const query = new URLSearchParams({
      product: productId,
      periode: preset,
      van: bereik.van,
      tot: bereik.tot,
    });
    void navigate(`/prijsgrafiek?${query.toString()}`);
  }

  async function exporteer(): Promise<void> {
    setExporting(true);
    try {
      const query = new URLSearchParams({ van: bereik.van, tot: bereik.tot });
      if (alleenWijzigingen) query.set('alleenWijzigingen', 'true');

      const response = await fetch(`/api/export/historie/${productId}?${query.toString()}`, {
        credentials: 'same-origin',
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.error?.message ?? 'De export kon niet worden gemaakt.');
      }
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download =
        response.headers.get('content-disposition')?.match(/filename="([^"]+)"/)?.[1] ??
        'prijsmonitor-historie.xlsx';
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'De export kon niet worden gemaakt.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className={ui.page}>
      {error && <Notice tone="error">{error}</Notice>}

      <div className={styles.controls}>
        <select
          className={styles.select}
          value={productId}
          onChange={(e) => update({ product: e.target.value })}
          aria-label="Product"
        >
          {products.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} · {p.ownSku}
            </option>
          ))}
        </select>

        <div className={styles.presets} role="group" aria-label="Periode">
          {PERIODES.map((optie) => (
            <button
              key={optie.key}
              type="button"
              className={preset === optie.key ? styles.presetActive : styles.preset}
              onClick={() => update({ periode: optie.key, van: '', tot: '' })}
            >
              {optie.label}
            </button>
          ))}
        </div>

        <label className={styles.dateField}>
          van
          <input
            type="date"
            value={bereik.van}
            onChange={(e) => update({ periode: 'custom', van: e.target.value, tot: bereik.tot })}
          />
        </label>
        <label className={styles.dateField}>
          t/m
          <input
            type="date"
            value={bereik.tot}
            onChange={(e) => update({ periode: 'custom', van: bereik.van, tot: e.target.value })}
          />
        </label>

        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={alleenWijzigingen}
            onChange={(e) => update({ alleenWijzigingen: e.target.checked ? 'true' : '' })}
          />
          Alleen dagen met een wijziging
        </label>

        <div className={styles.presets} role="group" aria-label="Volgorde van de webshops">
          <button
            type="button"
            className={opNaam ? styles.preset : styles.presetActive}
            onClick={() => update({ sortering: '' })}
            title="Goedkoopste webshop vooraan, op de prijs van de bovenste rij"
          >
            Op prijs
          </button>
          <button
            type="button"
            className={opNaam ? styles.presetActive : styles.preset}
            onClick={() => update({ sortering: 'naam' })}
          >
            Op naam
          </button>
        </div>

        <span style={{ flex: 1 }} />
        {/*
          De weg terug naar de grafiek, met hetzelfde product en dezelfde periode. Zonder dit moest
          je daar het product en de periode opnieuw kiezen — en dan vergelijk je twee dingen die niet
          meer over dezelfde vraag gaan.
        */}
        <Button variant="secondary" onClick={naarGrafiek} disabled={!productId}>
          Grafiekweergave
        </Button>
        <Button onClick={() => void exporteer()} busy={exporting} disabled={!history}>
          Exporteer naar Excel
        </Button>
      </div>

      {history && (
        <>
          <p className={styles.summary}>
            {history.summary.dayCount} {history.summary.dayCount === 1 ? 'dag' : 'dagen'} ·{' '}
            {history.summary.changeDayCount}{' '}
            {history.summary.changeDayCount === 1
              ? 'dag met een prijswijziging'
              : 'dagen met een prijswijziging'}
          </p>

          {history.days.length === 0 ? (
            <div className={ui.cardPadded}>
              <p className={ui.empty}>Voor deze periode zijn nog geen metingen vastgelegd.</p>
            </div>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Dag</th>
                    {ordening.map((index) => {
                      const column = history.columns[index];
                      if (!column) return null;
                      return (
                        <th key={`kolom-${index}`} className={column.isOwn ? styles.ownHeader : ''}>
                          <BronLabel
                            naam={column.isOwn ? 'Eigen webshop' : column.name}
                            isOwn={column.isOwn}
                            url={column.url}
                          />
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {history.days.map((day) => (
                    <tr key={day.dateKey}>
                      <td className={styles.dayCell}>{formatDayLabel(day.dateKey)}</td>
                      {ordening.map((kolomIndex) => {
                        const cell = day.cells[kolomIndex];
                        if (!cell) return <td key={`cel-${kolomIndex}`} />;
                        if (cell.priceCents === null) {
                          return (
                            <td key={cell.sourceId} className={styles.empty}>
                              — leeg
                              {cell.status && cell.status !== 'SUCCESS' && (
                                <span className={styles.statusHint}>
                                  {MEASUREMENT_STATUS_LABELS[cell.status]}
                                </span>
                              )}
                            </td>
                          );
                        }
                        const omhoog = cell.isChange && cell.changeDirection === 'UP';
                        const omlaag = cell.isChange && cell.changeDirection === 'DOWN';
                        const klassen = [
                          cell.isLowest ? styles.lowest : '',
                          !cell.isLowest && omhoog ? styles.up : '',
                          !cell.isLowest && omlaag ? styles.down : '',
                        ]
                          .filter(Boolean)
                          .join(' ');
                        return (
                          <td key={cell.sourceId} className={klassen || styles.neutral}>
                            {cell.isChange && (
                              <span className={omhoog ? styles.arrowUp : styles.arrowDown}>
                                {omhoog ? '▲' : '▼'}{' '}
                              </span>
                            )}
                            {formatEuro(cell.priceCents)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className={styles.legend}>
            <span>
              <span className={styles.swatchLowest} /> laagste prijs die dag
            </span>
            <span>
              <strong style={{ color: 'var(--c-up)' }}>▲</strong> hoger dan de vorige dag
            </span>
            <span>
              <strong style={{ color: 'var(--c-down)' }}>▼</strong> lager dan de vorige dag
            </span>
            <span>
              <strong style={{ color: 'var(--c-empty)' }}>— leeg</strong> meting mislukt
            </span>
          </div>
        </>
      )}
    </div>
  );
}
